<script>
export default {
  name: "Select",
  props: {
    modelValue: {
      type: [String, Number],
      default: null,
      validator: (value) => value === null || typeof value === 'string' || typeof value === 'number'
    },
    options: {
      type: Array,
      default: () => []
    },
    optionValue: {
      type: String,
      default: 'id'
    },
    optionLabel: {
      type: String,
      default: 'name'
    },
    placeholder: {
      type: String,
      default: 'Seleccionar...'
    },
    label: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false
    };
  },
  computed: {
    selectedLabel() {
      if (this.modelValue === null || this.modelValue === undefined || this.modelValue === '') {
        return '';
      }
      
      const option = this.options.find(opt => 
        this.getOptionValue(opt) === this.modelValue
      );
      
      return option ? this.getOptionLabel(option) : '';
    },
    buttonClasses() {
      return {
        'border-n-weak text-n-slate-12': !this.isOpen && !this.disabled,
        'ring-2 ring-n-brand border-n-brand': this.isOpen && !this.disabled,
        'opacity-60 cursor-not-allowed bg-n-slate-3/30': this.disabled,
        'cursor-pointer': !this.disabled
      };
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    toggle() {
      if (!this.disabled) {
        this.isOpen = !this.isOpen;
      }
    },
    getOptionValue(option) {
      // Si es un objeto, usar la propiedad optionValue
      if (typeof option === 'object' && option !== null) {
        return option[this.optionValue];
      }

      return option;
    },
    getOptionLabel(option) {
      // Si es un objeto, usar la propiedad optionLabel
      if (typeof option === 'object' && option !== null) {
        return option[this.optionLabel];
      }

      return String(option);
    },
    isSelected(option) {
      return this.getOptionValue(option) === this.modelValue;
    },
    selectOption(option) {
      if (this.disabled) return;
      
      const value = this.getOptionValue(option);
      this.$emit('update:modelValue', value);
      this.$emit('change', value);
      this.isOpen = false;
    },
    handleClickOutside(event) {
      // Cerrar dropdown si se hace click fuera del componente
      if (this.$el && !this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    }
  }
};
</script>

<template>
  <div class="relative w-full">
    <label 
      v-if="label" 
      class="block text-sm font-medium text-n-slate-12 mb-2"
    >
      {{ label }}
      <span v-if="required" class="text-n-ruby-10">*</span>
    </label>

    <button
      type="button"
      @click="toggle"
      :disabled="disabled"
      class="w-full px-4 py-3 border rounded-lg text-left flex items-center justify-between
             focus:ring-2 focus:ring-n-brand focus:border-n-brand outline-none transition-all
             bg-white dark:bg-n-solid-2 text-sm"
      :class="buttonClasses"
    >
      <span :class="{ 'text-n-slate-11': !selectedLabel, 'text-n-slate-12': selectedLabel }">
        {{ selectedLabel || placeholder }}
      </span>
      
      <svg 
        class="w-4 h-4 text-n-slate-11 transition-transform duration-200 flex-shrink-0 ml-2" 
        :class="{ 'rotate-180': isOpen }"
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <ul
      v-if="isOpen && options.length > 0"
      class="absolute left-0 right-0 bg-white dark:bg-n-solid-2 
             border border-n-weak rounded-lg shadow-lg 
             max-h-60 overflow-y-auto overflow-x-hidden z-50 mt-1"
      style="overscroll-behavior: contain; scrollbar-width: thin; scrollbar-color: rgba(148, 163, 184, 0.3) transparent;"
    >
      <li
        v-for="(option, index) in options"
        :key="getOptionValue(option) || index"
        @click="selectOption(option)"
        class="px-4 py-3 text-sm cursor-pointer hover:bg-n-slate-9/10 transition-colors
               text-n-slate-12 whitespace-nowrap"
        :class="{ 'bg-n-brand/5 font-medium': isSelected(option) }"
      >
        {{ getOptionLabel(option) }}
      </li>
    </ul>

    <ul
      v-if="isOpen && options.length === 0"
      class="absolute left-0 right-0 bg-white dark:bg-n-solid-2 
             border border-n-weak rounded-lg shadow-lg z-50 mt-1"
    >
      <li class="px-4 py-3 text-sm text-n-slate-11 text-center">
        No hay opciones disponibles
      </li>
    </ul>
  </div>
</template>

<style scoped>

ul::-webkit-scrollbar {
  width: 6px;
}

ul::-webkit-scrollbar-track {
  background: transparent;
}

ul::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.3);
  border-radius: 3px;
}

ul::-webkit-scrollbar-thumb:hover {
  background-color: rgba(148, 163, 184, 0.5);
}
</style>
